import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import HelpSectionImage from '../assets/help_section.f61f8984.jpg';
import ThoughtsBookCover from '../assets/thoughts_book_cover.444d3ee2.jpg';
import TwinBrainBookCover from '../assets/twin_brain_book_cover.fbe0d9e5.jpg';

const EAPServicesSection: React.FC = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const benefits = [
    {
      title: "Enhance Employee Well-Being",
      description: "An EAP provides mental health support, stress management resources, and counseling services, helping employees cope with personal and professional challenges. This support improves overall well-being, creating a healthier workplace environment."
    },
    {
      title: "Boost Productivity and Engagement", 
      description: "By addressing personal and work-related issues, EAPs help employees remain focused and motivated. This leads to improved productivity and job satisfaction, as employees feel more supported and valued by their organization."
    },
    {
      title: "Reduce Absenteeism and Turnover",
      description: "EAPs offer timely help for employees dealing with stress or life challenges, reducing absenteeism. With a supportive workplace, employees are more likely to stay committed, reducing turnover rates effectively."
    }
  ];

  const supportAreas = [
    "Reduce barrier to returning to work or to maintain attendance and improve performance at work.",
    "Identify additional support in the workplace.",
    "Reduce barriers against work stress.",
    "Take advantage of early intervention to prevent or reduce small issues at work or at home from evolving into big impacts on their performance or into sick absence and high production or service delivery costs."
  ];

  const assessmentQuestions = [
    {
      id: 1,
      question: "I have stress, anxiety or other psychological conditions in my personal life, my family life, workplace, employment, business etc."
    },
    {
      id: 2,
      question: "I am not good enough. Or I feel that I am a failure or an embarrassment to myself or to my family and this is affecting my job/work life."
    },
    {
      id: 3,
      question: "I have a difficulty or a conflict in my relationship with someone at work that I want to explore deeper. Or I am struggling to deal with a major change in my life and it is affecting my work/job life."
    },
    {
      id: 4,
      question: "I want a safe space to talk how I am feeling and thinking about my difficulties at work."
    }
  ];

  const faqItems = [
    {
      question: "What is an Employee Assistance Program (EAP)?",
      answer: "An Employee Assistance Program (EAP) is a confidential workplace benefit designed to help employees deal with personal and professional challenges that may impact their work performance, health, and well-being."
    },
    {
      question: "How does the EAP counseling work?",
      answer: "Our EAP provides structured counseling sessions with qualified professionals. Sessions can be conducted face-to-face, via telephone, email, or live chat based on your preference and needs."
    },
    {
      question: "Is the EAP service confidential?",
      answer: "Yes, all EAP services are completely confidential. Your employer will not have access to the details of your sessions or any information you share with our counselors."
    },
    {
      question: "How many counseling sessions can I access?",
      answer: "The number of sessions varies depending on your employer's EAP package. Typically, employees have access to a set number of sessions per year, with the option to extend if needed."
    },
    {
      question: "What issues can the EAP help with?",
      answer: "The EAP can help with a wide range of issues including stress, anxiety, depression, relationship problems, work-life balance, financial concerns, and substance abuse issues."
    }
  ];

  const testimonials = [
    {
      text: "The EAP service provided by Health City has been invaluable to our employees. The confidential support has helped many of our team members through difficult times.",
      author: "HR Director, Tech Company"
    },
    {
      text: "I was hesitant to use the EAP at first, but the counselor was so understanding and helpful. It really made a difference in managing my work stress.",
      author: "Employee, Financial Services"
    },
    {
      text: "Our company has seen a significant reduction in absenteeism since implementing Health City's EAP. The flexible counseling options work perfectly for our remote workforce.",
      author: "Operations Manager, Retail"
    }
  ];

  return (
    <div className="bg-white py-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Left Column - Text Content */}
          <div className="space-y-8">
            <h2 className="text-3xl lg:text-4xl font-bold text-black">
              We Help To Plan And Support Staff To:
            </h2>
            
            <ul className="space-y-4">
              {supportAreas.map((area, index) => (
                <li key={index} className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-black rounded-full mt-3 mr-4 flex-shrink-0"></span>
                  <span className="text-xl text-black leading-relaxed">{area}</span>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Right Column - Image */}
          <div className="flex justify-center items-center">
            <div className="w-full max-w-lg">
              <img 
                src={HelpSectionImage} 
                alt="Employee assistance and support"
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>

        {/* Benefits Section Title */}
        <div className="text-center mb-12">
          <h2 className="text-2xl lg:text-3xl font-normal text-black">
            Personalized Care for a Thriving, Energized Workforce
          </h2>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {benefits.map((benefit, index) => (
            <div key={index} className="space-y-4">
              <h3 className="text-xl font-bold text-black">
                {benefit.title}
              </h3>
              <p className="text-black leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>

        {/* Structured EAP Section */}
        <div className="text-center mb-8">
          <button className="text-3xl lg:text-4xl font-bold text-black hover:text-gray-700 transition-colors">
            Structured EAP Counseling for Your Employees
          </button>
        </div>

        {/* Detailed Description */}
        <div className="max-w-5xl mx-auto">
          <div className="text-xl text-black leading-relaxed space-y-6">
            <p>
              Our Employee Assistance Program (EAP) delivers structured counseling to support your staff's mental well-being. 
              With an in-house team of expert counselors and a broad network of active professionals across the UK, we ensure 
              your employees receive the care they need. Recognizing the diverse needs of your workforce, we offer:
            </p>
            
            <ul className="space-y-2 ml-6">
              <li className="flex items-start">
                <span className="mr-2">-</span>
                <span>Flexible counseling options beyond face-to-face sessions</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">-</span>
                <span>Structured therapy with tailored sessions per person</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">-</span>
                <span>Confidential email support, or convenient telephone and live chat counseling</span>
              </li>
            </ul>
            
            <p>
              Empower your team to overcome personal challenges, reduce workplace stress, and improve overall productivity 
              with a confidential, professional service they can trust.
            </p>
            
            <p>
              Our EAP is a commitment to employee wellness and a vibrant work culture. It acts as a trusted friend, 
              a helping hand, and a wise counselor, assisting employees to navigate life's challenges, enhancing their 
              productivity, and ultimately, their happiness.
            </p>
          </div>
        </div>
      </div>

      {/* What Are You Looking For? Section */}
      <section className="py-20 bg-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">What Are You Looking For?</h2>
          
          <div className="space-y-6">
            {assessmentQuestions.map((item, index) => (
              <div key={item.id} className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6">
                <div className="flex gap-4">
                  <div className="text-2xl font-bold text-black opacity-20 shrink-0">
                    {String(item.id).padStart(2, '0')}
                  </div>
                  <div className="flex-1">
                    <p className="text-lg text-white mb-4">{item.question}</p>
                    <div className="flex gap-4">
                      <button className="bg-gray-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-500 transition-colors">
                        Yes
                      </button>
                      <button className="bg-gray-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-500 transition-colors">
                        No
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <button 
              onClick={() => navigate('/contact')}
              className="bg-gray-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-500 transition-colors"
            >
              NEXT
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Frequently Asked Questions</h2>
          
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200">
                <button 
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full p-6 text-left"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">{item.question}</h3>
                    <ChevronDown 
                      className={`h-5 w-5 text-gray-400 transition-transform ${expandedFaq === index ? 'rotate-180' : ''}`} 
                    />
                  </div>
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-700 leading-relaxed">{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet The Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Meet The Team</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* See All Team Button - Left Side */}
            <div className="flex items-center justify-center lg:justify-start">
              <button 
                onClick={() => navigate('/teams')}
                className="bg-gray-700 hover:bg-gray-800 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
              >
                SEE ALL TEAM
              </button>
            </div>

            {/* Team Description - Right Side */}
            <div className="bg-gray-200 rounded-lg p-8">
              <p className="text-base text-gray-800 leading-relaxed">
                HealthCity's core team of its associates include qualified psychologists, therapists, nurses, doctors and other professionals, as 
                well as, former service users. They have relevant training, skills, and experience in mental health and physical wellbeing and its recovery. They work 
                with persons/patients or their families in the community to support their members. Behind the experienced directors and managers below 
                stands a number of associates, dedicated compassionate directors and advisors, clinicians, University researchers and client support staff 
                who make us all look good and achieving.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action - Services Starting From £65 */}
      <section className="py-20 bg-slate-700 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Our Services Are Starting From As Low As £65
          </h2>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/contact')}
              className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-colors"
            >
              Book Now
            </button>
          </div>
        </div>
      </section>

      {/* Testimonials Section - What Our Clients Say */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">What Our Clients Say About Us</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-200 rounded-lg p-8 relative">
                <div className="text-6xl text-gray-400 mb-4">"</div>
                <p className="text-gray-700 text-sm mb-6 leading-relaxed">
                  {testimonial.text}
                </p>
                <p className="text-gray-900 font-semibold text-sm">
                  – {testimonial.author}
                </p>
              </div>
            ))}
          </div>

          <div className="flex gap-4 justify-start">
            <button className="bg-gray-400 hover:bg-gray-500 text-white p-3 rounded transition-colors">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button className="bg-gray-400 hover:bg-gray-500 text-white p-3 rounded transition-colors">
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      </section>

      {/* Books Section */}
      <section className="py-20 bg-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            {/* Book 1 - Thought Techniques */}
            <div className="relative h-[600px] flex items-start justify-center pt-12">
              {/* Book image - bottom layer, moved up 50% and left 50% */}
              <div className="absolute -left-20 -top-12 w-80 h-[91%] rounded-lg shadow-2xl z-10 overflow-hidden">
                <img 
                  src={ThoughtsBookCover} 
                  alt="Thought Techniques for Simple Brain Files" 
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Green gradient content box - top layer, 50% opacity, centered */}
              <div className="relative w-80 h-[97.5%] bg-gradient-to-br from-green-500/50 to-green-700/50 rounded-lg shadow-xl z-20 flex items-center justify-center px-8">
                <div className="text-white text-center">
                  <h3 className="text-2xl md:text-3xl font-bold mb-6 leading-tight">
                    Thought Techniques for Simple Brain Files
                  </h3>
                  <button className="bg-white text-gray-900 px-8 py-3 rounded font-bold hover:bg-gray-100 transition-colors">
                    DOWNLOAD
                  </button>
                </div>
              </div>
            </div>

            {/* Book 2 - Twin Brain */}
            <div className="relative h-[600px] flex items-start justify-center pt-12">
              {/* Book image - bottom layer, moved up 50% and left 50% */}
              <div className="absolute -left-20 -top-12 w-80 h-[91%] rounded-lg shadow-2xl z-10 overflow-hidden">
                <img 
                  src={TwinBrainBookCover} 
                  alt="Do I Have a Twin Brain?" 
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Blue gradient content box - top layer, 50% opacity, centered */}
              <div className="relative w-80 h-[97.5%] bg-gradient-to-br from-blue-500/50 to-blue-700/50 rounded-lg shadow-xl z-20 flex items-center justify-center px-8">
                <div className="text-white text-center">
                  <h3 className="text-2xl md:text-3xl font-bold mb-6 leading-tight">
                    Do I Have a Twin Brain?
                  </h3>
                  <button className="bg-white text-gray-900 px-8 py-3 rounded font-bold hover:bg-gray-100 transition-colors">
                    DOWNLOAD
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EAPServicesSection;